/**
 * RGR05 반품조회(전체) (라우트 rgr13) — 컴포넌트 모드 엔트리. (LOGISPRD-11237)
 *
 * 화면은 `handoff/page.generated.tsx`(컨덕터 생성본 — 재핸드오프 때 통째로 교체)가 그리고, 이 파일은 그 뷰의 Wiring 슬롯에
 * 옛 배선(조회·엑셀 2.0 엔드포인트, moms-be LOGISPRD-9561/9562)을 잇는다. UI 를 여기서 그리지 않는다.
 *
 * 조건에 걸린 전건을 BE 상한(1,000) 구간으로 이어 받아(LOGISPRD-15269) 페이징·목록검색은 뷰가 화면에서 한다 —
 * 서버 페이징으로 나누면 검색이 현재 페이지 안에서만 돌아 1.0 과 답이 달라진다.
 *
 * 규칙: 실패 = throw(모달 유지), search 의 false = 조회 안 함(뷰가 페이저·선택을 되돌리지 않는다).
 *
 * 생성본을 직접 고쳤다(LOGISPRD-14889) — 엑셀 모달의 '파일 비밀번호' 칸(RGR09 생성본과 같은 요소)·열 때 비우는 필드에 password.
 * 컨덕터 데모에 아직 없다 — 재핸드오프로 덮이면 비밀번호 칸이 사라져 다운로드가 전부 막힌다. seam.test 가 잡는다.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { invalidError, notice } from '@/components/pbo/notice'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { API_COMMON_CACHE_TIME, DOWNLOAD_REASON_EMPTY_MESSAGE } from '@/utils/consts'
import { toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { getShipperListV2 } from '@/services/shipper/apis'
import { ShipperItem } from '@/services/shipper/types'
import { buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, type ShipperScope, scopeShippers, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { returnInboundQueryKeys, useGetAllReturnInbounds } from '@/services/return/returnInbound/useReturnInboundQueries'
import { RETURN_INBOUND_DOWNLOAD_URL } from '@/services/return/returnInbound/apis'
import { ORDER_STATUS_OPTIONS, ReturnInboundDownloadParams, toSearchPayload } from '@/services/return/returnInbound/types'
import { isValidDownloadPassword, MESSAGES as RGR02_MESSAGES } from '@/pages/return/rgr02V2/rules'
import { EXCEL_FILE_NAME, MESSAGES, REASON_DETAIL_MIN_LENGTH, SHIPPER_LIST_FETCH_SIZE } from '@/pages/return/returnInquiryAll/constants'
import { applyClientFilter } from '@/pages/return/returnInquiryAll/filters'
import { type SearchCondition, asList, asText, toSearchCondition, toViewRow } from '@/pages/return/returnInquiryAll/rules'
import { fromSearchQuery, type Rgr13SearchQuery, toSearchQuery } from '@/pages/return/returnInquiryAll/searchQuery'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnInquiryAllPageView, { type ActionCtx, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { type CoMingleReturnProps, withCoMingleReturns } from '@/pages/return/coMingle'

const SHIPPER_LIST_KEY = [...returnInboundQueryKeys.all, 'SHIPPERS'] as const
/** 화주유형 옵션 빈 목록 — 옵션 슬롯은 렌더마다 불리므로 참조를 고정한다. */
const EMPTY_OPTIONS: { value: string; label: string }[] = []

/** 입고상태 옵션 — BE enum 값(데모 목록은 출고 값이라 쓰지 않는다). */
const ORDER_STATUS_VIEW_OPTIONS = ORDER_STATUS_OPTIONS.map((v) => ({ value: v, label: v }))

/** 오늘(로컬) YYYY-MM-DD. */
const today = () => new Date().toLocaleDateString('sv-SE')

// 그리드 코드 — 개인 컬럼 배치 키와 화면관리 그리드 설정이 같은 값을 쓴다(docs/conventions/grid-code.md)
const HEAD_GRID_CD = 'RGR13_HEAD'

function ReturnInquiryAll({ coMingleEnabled }: CoMingleReturnProps) {
  // ── 화주명 목록 — 전 화주를 한 번 받아 고른 화주유형으로 좁힌다(화주 범위 강제는 BE allowedShipperCodes 몫 — 아래 잠금은 화면 일관성용).
  const { data: shippers, error: shippersError, refetch: refetchShippers } = useQuery<ShipperItem[], Error>({
    queryKey: SHIPPER_LIST_KEY,
    queryFn: async () => (await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: SHIPPER_LIST_FETCH_SIZE }))?.data?.items ?? [],
    staleTime: API_COMMON_CACHE_TIME,
    // 전역 QueryCache.onError 알럿은 끈다 — 아래 effect 의 화면 문구 하나만 띄운다(안 끄면 알럿 2개).
    meta: { noErrorMessage: true },
  })
  // 화주 목록 조회 실패 — 화주명 선택지가 빈 채로 남으므로 이유를 알린다.
  useEffect(() => {
    if (shippersError) notice.failure(MESSAGES.shippersFailed)
  }, [shippersError])

  /*
   * 화주 잠금(LOGISPRD-14949) — 1.0 RGR05 FilterBar 와 같이 통합 운영 화주(FREE_SHIPPERS) 외 계정은 화주유형·화주명을
   *  고를 수 있는 화주로 고정하고 화주유형 칸을 잠근다. 세션은 마운트 뒤 안 바뀐다(마운트 스냅샷). 화주명 옵션 value 가 화주 **이름**이라
   *  화주 이름은 받은 화주 목록(코드로 찾음)에서 읽고, 목록이 오기 전엔 세션 화주명으로 둔다. 유형은 목록이 와야 안다(filterFallback 이 채운다).
   * 재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르고 기본값은 MUSINSA 다. 고른 화주 그대로 조회한다(LOGISPRD-15725, `withCoMingle`).
   */
  const scopeRef = useRef<ShipperScope>()
  scopeRef.current ??= withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingleEnabled)
  const scope = scopeRef.current
  const scopedShippers = useMemo(() => (shippers ? scopeShippers(shippers, scope) : undefined), [shippers, scope])
  /** 잠금 계정이 고를 수 있는 화주의 이름 — 목록에 없으면 세션 값 */
  const lockedNames = useMemo(
    () => (scope.locked ? selectableShippers(scope).map((s) => scopedShippers?.find((r) => r.shipperCode === s.shipperCode)?.shipperName || s.shipperName) : []),
    [scope, scopedShippers],
  )
  const defaultName = scope.locked ? lockedNames[selectableShippers(scope).findIndex((s) => s.shipperCode === defaultShipper(scope).shipperCode)] : undefined
  /** 잠금 계정의 화주명 값 — 고를 수 있는 화주만 남기고, 하나도 없으면 기본 화주다 */
  const lockedShipperNames = (names: string[]): string[] => {
    const kept = names.filter((n) => lockedNames.includes(n))
    if (kept.length) return [...new Set(kept)]
    return defaultName ? [defaultName] : []
  }
  /** 잠금 계정이 고른 화주의 유형 — 목록이 오기 전엔 빈 목록 */
  const lockedServiceTypes = (names: string[]): string[] => [
    ...new Set(lockedShipperNames(names).map((n) => scopedShippers?.find((r) => r.shipperName === n)?.logisticsServiceType || '').filter(Boolean)),
  ]
  // 옵션 슬롯은 매 렌더 불리고 뷰의 filterFallback effect 가 옵션 배열 참조를 의존성으로 보므로 화주별로 참조를 고정한다.
  const lockedServiceTypeOptions = useMemo(
    () =>
      new Map(
        lockedNames.map((name) => {
          const type = scopedShippers?.find((r) => r.shipperName === name)?.logisticsServiceType
          return [name, type ? [{ value: type, label: type }] : []] as const
        }),
      ),
    [lockedNames, scopedShippers],
  )
  /** 잠금 계정은 어떤 경로(URL 복원·조회영역)로 와도 고를 수 있는 화주와 그 유형으로 조회한다. 고른 화주를 바꾸지 않는다. */
  const lockCondition = (c: SearchCondition): SearchCondition => {
    if (!scope.locked) return c
    const shipperNames = lockedShipperNames(c.params.shipperNames ?? [])
    return { ...c, params: { ...c.params, shipperNames, logisticsServiceTypes: lockedServiceTypes(shipperNames) } }
  }

  // 엑셀 다운로드 사유코드 — 공통코드 콤보(DOWNLOAD_CD, LOGISPRD-14079). useMemo 라 매 렌더 같은 배열.
  const { data: combos, isPending: isCombosPending } = useGetCombos(['DOWNLOAD_CD'], { errorToast: true })
  const reasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])

  // 조회조건 세션 유지 — [조회] 때 메뉴 세션·URL 에 남기고(reload 로 탭바 갱신) 마운트 때 URL 에서 한 번만 읽는다(rgr01V2 방식)
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  const [restored] = useState(() => fromSearchQuery(parsedQs))
  const persistSearch = (query: Rgr13SearchQuery) => {
    MenuStorage.menuOnSession.setSearch(query)
    menuSession.reload()
    replaceQuery(query)
  }

  // ── 조회 — [조회] 를 눌러야 조건이 선다(옛 화면도 진입 자동 조회 없음). 남겨 둔 조건으로 들어오면 그 조건으로 바로 조회한다 ──
  //  화주명을 고를 수 있는 잠금 계정(재고통합)은 화주 목록이 온 뒤 세운다 — 화주명 값이 이름이라 MUSINSA 이름을 목록에서 알아야 복원 값을 맞출 수 있다.
  const waitsShippers = scope.locked && canSelectShipper(scope)
  const restoredCondition = () => {
    const next = restored && toSearchCondition(restored)
    return next && !('invalid' in next) ? lockCondition(next) : null
  }
  const [condition, setCondition] = useState<SearchCondition | null>(() => (waitsShippers ? null : restoredCondition()))
  const restoreDeferredRef = useRef(waitsShippers)
  //  목록 조회가 실패하면 세우지 않는다 — MUSINSA 이름을 모르는 채 조회하게 된다. [조회] 가 목록을 다시 받고, 오면 이 effect 가 복원한다.
  useEffect(() => {
    if (!restoreDeferredRef.current || !shippers) return
    restoreDeferredRef.current = false
    setCondition((current) => current ?? restoredCondition())
  })
  // 실패 안내·10만 건 상한 안내는 훅(useFetchAllRows)이 띄운다 — 화면 effect 를 두면 알럿이 두 번 뜬다.
  const { items, isFirstLoading, isFetchingRest, error, refetch, guardAction } = useGetAllReturnInbounds(condition?.params ?? null)

  // 입고유형은 BE 가 받지 않아 화면에서 거른다(옛 화면 applyClientFilter 그대로). 입고일자는 서버 조건이다(LOGISPRD-14906).
  const viewRows = useMemo(() => applyClientFilter(items, { orderType: condition?.orderType }).map(toViewRow), [items, condition])

  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload<ReturnInboundDownloadParams>({
    fileNamePrefix: EXCEL_FILE_NAME,
    config: { method: 'post' },
  })

  /** 세션 화주명 — 받은 화주 목록에 있을 때만 [화주명]. 목록 응답 전·목록에 없음·세션 화주 없음은 undefined(뷰가 '전체'로 보인다). */
  const sessionShipperIfKnown = (): string[] | undefined => {
    if (scope.locked) return defaultName ? [defaultName] : undefined
    const name = String(AuthStorage.user.get().shipperName ?? '')
    return name && shippers?.some((s) => s.shipperName === name) ? [name] : undefined
  }

  // Wiring 은 매 렌더 새로 만든다 — 슬롯이 최신 상태·훅 값을 닫아 잡게(useMemo 로 고정하면 첫 렌더 클로저가 남는다).
  const wiring: Wiring = {
    permission: usePermissionSlot('rgr13', { rgr05Table: HEAD_GRID_CD }),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr05Table: coreTableLayoutSlot(HEAD_GRID_CD) },
    // 다운로드 중 = 바쁨. 지금 생성본엔 선택 게이트 버튼이 없어 잠기는 버튼은 없다(다운로드 중엔 MLoader 가 화면을 덮는다) —
    //  재핸드오프로 그런 버튼이 생기면 그대로 잠긴다.
    useBusy: () => isDownloading,
    rows: {
      // 클라이언트 페이징(데모와 같음) — total 을 주지 않아 '전체 N' 이 받는 만큼 늘어난다. 행 key = BE 행 id.
      //  첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390, 엑셀은 guardAction 이 막는다).
      ROWS: () => ({ rows: viewRows, loading: isFirstLoading || isFetchingRest }),
    },
    actions: {
      /** [조회] — 필수 칸(화주·입고예정일자)이 비면 안내하고 false(조회 안 함). 같은 조건이면 옛 화면처럼 캐시를 그대로 쓴다. */
      search: (filters) => {
        // 화주명을 고르는 잠금 계정은 화주 목록이 있어야 고른 이름을 맞춘다 — 없으면(실패) 목록을 다시 받고 조회하지 않는다. 실패 안내는 위 effect 가 띄운다
        if (waitsShippers && !shippers) {
          refetchShippers().catch(() => undefined)
          return false
        }
        const raw = toSearchCondition(filters)
        if ('invalid' in raw) {
          notice.invalid(raw.invalid)
          return false
        }
        const next = lockCondition(raw)
        // 화주유형을 바꿔도 이미 고른 화주명이 칸에 남는다(뷰는 값을 옵션과 대조하지 않는다) — 그 조합은 늘 0건이라 조회 전에 막는다(gr02·rgr02 준용).
        //  잠금 계정은 위 lockCondition 이 먼저 고른 화주의 유형으로 바꿔 두므로 이 대조를 그대로 통과한다.
        const { logisticsServiceTypes: types = [], shipperNames: names = [] } = next.params
        if (types.length && names.length && shippers?.length) {
          const allowed = new Set(buildShipperNameOptions(shippers, { valueKey: 'shipperName', serviceTypes: types }).map((o) => o.value))
          if (names.some((n) => !allowed.has(n))) {
            notice.invalid(MESSAGES.shipperNameNotInType)
            return false
          }
        }
        // 같은 조건이면 queryKey 가 같아 react-query 가 다시 부르지 않는다 — 성공 결과는 옛 화면처럼 그대로 쓰고,
        //  직전 결과가 실패였을 때만 다시 부른다(안 그러면 요청·로딩·알럿 없이 빈 표만 남는다).
        const sameCondition = condition !== null && JSON.stringify(condition.params) === JSON.stringify(next.params)
        setCondition(next)
        persistSearch(toSearchQuery({ ...filters, shipper_name: next.params.shipperNames }))
        // 실패 안내는 useFetchAllRows 의 전역 알럿(QueryCache.onError)이 refetch 실패마다 띄운다 — 여기선 결과를 기다리지 않는다.
        if (sameCondition && error) refetch().catch(() => undefined)
        return undefined
      },
      /**
       * 엑셀 다운로드 확인 — 뷰가 사유코드·상세사유(5자)가 찰 때까지 [확인]을 잠근다. 슬롯도 한 번 더 막고 비밀번호도 여기서 막는다(실패 = 안내 + throw, 모달 유지).
       * 엑셀은 BE 가 비밀번호 zip 으로 만들고 감사로그(사유)도 BE 가 남긴다(LOGISPRD-14889, RGR09 선례). 비밀번호 규칙은 BE `PasswordValidator` 미러(RGR02 rules).
       * 서버가 조회 조건(입고일자 기간 포함)으로 다시 뽑는다 — 화면 필터 입고유형은 반영되지 않는다. 페이징(page·size)은 다운로드 요청 필드가 아니라 뺀다.
       * TODO(api): 엑셀에 입고유형 조건을 실으려면 moms-be 파라미터 확장 필요.
       */
      rgr05DownloadModal_ok: async (ctx: ActionCtx) => {
        const f = ctx.form ?? {}
        const reasonCode = asText(f.reason)
        const detailedReason = asText(f.detail).trim()
        const password = asText(f.password)
        if (!reasonCode || detailedReason.length < REASON_DETAIL_MIN_LENGTH) throw invalidError(MESSAGES.downloadReasonInvalid)
        if (!isValidDownloadPassword(password)) throw invalidError(RGR02_MESSAGES.passwordInvalid)
        if (!condition) throw invalidError(MESSAGES.excelNoRows)
        const { page: _page, size: _size, ...filters } = toSearchPayload(condition.params)
        // 실패 안내는 useExcelDownload 가 띄운다.
        await onDownloadExcel(RETURN_INBOUND_DOWNLOAD_URL, { ...filters, reasonCode, detailedReason, password })
        return undefined
      },
    },
    opens: {
      /**
       * 목록 검색까지 거쳐 화면에 보이는 행이 0이면 열지 않는다(옛 화면 excelNoRows).
       * 사유코드 콤보가 비면(로딩 실패) [확인]이 영영 꺼지므로 열기 전에 이유를 알린다 — 옛 모달은 모달 안에 같은 문구를 띄웠다(LOGISPRD-14079).
       */
      rgr05ExcelButton: (ctx) => {
        // 받는 중·일부 실패면 화면 건수가 서버와 달라 막는다(안내는 guardAction 이 띄운다).
        if (!guardAction()) return false
        if ((ctx.visible ?? viewRows).length === 0) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        // 콤보 응답 전에는 연다(라디오가 곧 채워진다) — 받아 봤는데 비었을 때(실패 포함)만 막는다.
        if (!isCombosPending && reasonOptions.length === 0) {
          notice.failure(DOWNLOAD_REASON_EMPTY_MESSAGE)
          return false
        }
        return true
      },
    },
    options: {
      // 화주유형은 데모 6종 = SHIPPER_TYPES(옛 화면 상수와 같다). 잠금 계정은 고른 화주의 유형 하나(목록이 오기 전엔 빈 목록).
      RGR05SHIPPER_TYPE_OPTIONS: (demo, filters) => (scope.locked ? (lockedServiceTypeOptions.get(lockedShipperNames(asList(filters?.shipper_name))[0]) ?? EMPTY_OPTIONS) : demo),
      // 화주명은 화주 목록 API 에서 — 현재 고른 화주유형으로 좁힌다(유형 '전체' = 전 화주). value = 화주명(BE shipperNames 계약). 잠금 계정은 고를 수 있는 화주.
      //  잠금 계정은 유형으로 좁히지 않는다 — 유형 칸이 고른 화주를 따르므로 좁히면 다른 화주(MUSINSA)를 고를 수 없다(gr02V2 준용).
      RGR05SHIPPER_NAME_OPTIONS: (_demo, filters) =>
        buildShipperNameOptions(scopedShippers ?? [], { valueKey: 'shipperName', serviceTypes: scope.locked ? [] : asList(filters?.service_type) }),
      // 입고유형은 옛 화면 상수(services/return/returnInbound/types ORDER_TYPE_OPTIONS)와 데모 목록이 같다.
      RGR05ORDTYPE_OPTIONS: (demo) => demo,
      // 입고상태는 BE InboundReportStatus 5종 — 데모(출고완료·미출고·가송장)는 BE 가 거절한다(LOGISPRD-14464).
      RGR05ORDSTATUS_OPTIONS: () => ORDER_STATUS_VIEW_OPTIONS,
      REASONRADIO_OPTIONS: () => reasonOptions,
    },
    /**
     * 조회 조건 기본값 — 화주명 = 세션 화주(잠금 계정은 기본 화주 `defaultShipper`) · 화주유형 '전체'(옛 화면 SPR_NM 초기값, LOGISPRD-14460) · 입고예정일자 오늘
     * (날짜 칸 effect 는 필터를 접으면 돌지 않아 여기서 채운다). 화주명 옵션 value 가 화주명이라 코드가 아닌 이름을 넣는다.
     * 받은 화주 목록에 세션 화주가 없으면 비워 둔다('전체' 표시) — 목록 응답 전(진입)이면 비워 두고 아래 filterFallback 이 채운다.
     */
    //  잠금 계정은 화주유형도 기본 화주의 유형(목록이 아직 안 왔으면 빈 값 — filterFallback 이 채운다).
    filterDefaults: {
      rgr05FilterCard: (demo) => ({
        ...demo,
        shipper_name: sessionShipperIfKnown(),
        ...(scope.locked ? { service_type: lockedServiceTypes([]) } : {}),
        PLN_DATE: [today(), today()],
      }),
    },
    /**
     * 진입 초기값 — URL 에 남겨 둔 조건. 화주명이 늘 배열로 복원돼 아래 filterFallback 이 세션 화주로 덮지 않는다(복원 값이 기본값보다 앞선다).
     * 잠금 계정은 URL 의 화주명이 고를 수 있는 화주가 아니면 기본 화주로 덮고 화주유형은 버린다 — 유형 칸이 잠겨 있어 고칠 수 없고, 진입 조회(위 condition)도 같은 값으로 나간다.
     */
    filterInitial: {
      rgr05FilterCard: (defaults) => {
        if (!restored) return defaults
        if (!scope.locked) return { ...defaults, ...restored }
        const { service_type: _t, shipper_name: names, ...rest } = restored
        return { ...defaults, ...rest, service_type: lockedServiceTypes(asList(names)), shipper_name: lockedShipperNames(asList(names)) }
      },
    },
    /**
     * 진입 때 화주 목록이 늦게 오면 그때 세션 화주를 채운다. 화주명이 undefined(아직 아무도 안 건드림)일 때만 —
     * 사용자가 고르거나 지우면 뷰가 배열(전체 = [''])로 두므로, 옵션이 바뀔 때마다 다시 불려도 그 값을 덮지 않는다.
     */
    filterFallback: {
      rgr05FilterCard: (current, options) => {
        // 잠금 계정 — 화주유형은 목록이 온 뒤에야 정해진다. 비어 있을 때만 고른 화주의 유형으로 채운다(값이 있는 키는 덮지 않는다).
        if (scope.locked) {
          const types = lockedServiceTypes(asList(current.shipper_name))
          return types.length && asList(current.service_type).length === 0 ? { service_type: types } : undefined
        }
        if (current.shipper_name !== undefined) return undefined
        const shipper_name = sessionShipperIfKnown()
        // 목록을 기다리는 사이 화주유형을 골랐으면 그 유형의 화주명 옵션에 있을 때만 — 어긋나면 [조회]가 막힌다.
        const inType = shipper_name && options.RGR05SHIPPER_NAME_OPTIONS?.some((o) => o.value === shipper_name[0])
        return inType ? { shipper_name } : undefined
      },
    },
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·fallback·lockCondition 이 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15725)
     */
    filterDisabled: scope.locked ? { service_type: true, shipper_name: !canSelectShipper(scope) } : undefined,
    /** [초기화] — 조회 결과도 비운다(옛 handleReset). 남겨 둔 조회 조건(세션·URL)도 지운다. 조회영역 값은 뷰가 filterDefaults 로 되돌린다. */
    onReset: () => {
      setCondition(null)
      persistSearch({})
    },
  }

  return <ReturnInquiryAllPageView wiring={wiring} />
}

export default withCoMingleReturns(ReturnInquiryAll)
